import { Attachment } from 'types/Attachment';
import { Operation } from 'types/Operation';
import { PaginationParams, SortParams } from 'types/Pagination';

export enum DossierStatus {
  EtudeEnCours = 'Etude en cours',
  OffreEmise = 'Offre émise',
  OffreAcceptee = 'Offre acceptée',
  ChantierEnCours = 'Chantier en cours',
  DocumentsEnAttente = 'Documents en attente',
  Recu = 'Dossier reçu',
  EnAttenteDeControle = 'En attente de passage bureau de contrôle',
  Incomplet = 'Dossier incomplet',
  Valide = 'Dossier validé',
  PrimeProPayee = 'Prime Pro payée',
  PrimeBeneficiairePayee = 'Part Bénéficiaire payée',
  PrimeGlobalePayee = 'Prime Globale Payée',
  Refuse = 'Refusé',
  Abandon = 'Abandon',
  DemandeInformations = "Demande d'informations",
  EnCoursDeValidation = 'Dossier en cours de validation',
  Termine = 'Terminé',
  PrimeVersee = 'Prime versée',
  RecuperationDesDocumentsEnCours = 'Récupération des documents en cours',
  InformationsComplementairesRequises = 'Informations complémentaires requises',
}

export enum DossierSortFields {
  id = 'id',
  raisonSociale = 'beneficiaire_raisonSociale',
  beneficiaire = 'beneficiaire',
  nomSite = 'site_nom',
  adresse = 'site_adresse',
  date = 'dateModification',
  dateFinTravaux = 'chantier_dateFinTravaux',
  referenceChantier = 'chantier_reference',
  numeroDevis = 'chantier_numeroDevis',
  montantPrimes = 'chantier_montant',
  statut = 'statut',
  referentCommercial = 'referentCommercial',
}

export enum DossierPaymentTag {
  DemandeComptable = 'Demande comptable à émettre par Hellio',
  DocumentAttentePaiement = 'Document en attente pour paiement',
  PaiementEnCours = 'Paiement en cours de traitement',
}

export const getFilterDossierStatus = (isConcessionnaire: boolean) => {
  return [
    [
      DossierStatus.EtudeEnCours,
      DossierStatus.OffreEmise,
      DossierStatus.OffreAcceptee,
      DossierStatus.ChantierEnCours,
    ],
    [
      DossierStatus.DocumentsEnAttente,
      DossierStatus.EnCoursDeValidation,
      DossierStatus.EnAttenteDeControle,
      DossierStatus.Valide,
      DossierStatus.Termine,
    ],
    [
      DossierStatus.PrimeVersee,
      DossierStatus.Incomplet,
      DossierStatus.Refuse,
      DossierStatus.Abandon,
    ],
    ...(isConcessionnaire
      ? [
          [
            DossierStatus.InformationsComplementairesRequises,
            DossierStatus.DemandeInformations,
          ],
        ]
      : []),
  ];
};

export const DossierStatusKeys = Object.fromEntries(
  Object.entries(DossierStatus).map(([key, value]) => [value, key]),
);

export interface Base64File {
  name: string;
  content: string;
  type?: string;
}

export interface DossierHistoryItem {
  date: string;
  value: DossierStatus;
}

export class Entreprise {
  readonly raisonSociale?: string;
  readonly siret?: string;
  readonly adresse?: string;
  readonly codePostal?: string;
  readonly ville?: string;
  readonly email?: string;
  readonly telephone?: string;
}

export interface Chantier {
  adresse?: string;
  ville?: string;
  codePostal?: string;
  reference?: string;
  montant?: string | number;
  entreprise?: Entreprise;
  natureTravaux?: string;
  dateDebutTravaux?: string;
  dateFinTravaux?: string;
  dateEngagement?: string;
  dateDuRAI?: string;
  numeroDevis?: string;
}

export interface Site {
  nom?: string;
  usage?: string;
  adresse?: string;
  codePostal?: string;
  ville?: string;
}

export interface Batiment {
  ancien?: boolean;
  ancienneEnergieChauffage?: string;
  nouvelleEnergieChauffage?: string;
  surfaceChauffee?: number;
  surfaceChauffeeRehabilitee?: number;
  surfaceVentileeRehabilitee?: number;
}

export interface Personne {
  firstname?: string;
  lastname?: string;
  email?: string;
}
export interface Beneficiaire extends Personne {
  nom?: string;
  prenom?: string;
  raisonSociale?: string;
  siren?: string;
  adresse?: string;
  codePostal?: string;
  ville?: string;
  civilite?: string;
  telephone?: string;
}

export interface DossierDetails {
  id: string;
  statut: DossierStatus;
  dateModification?: Date;
  dateCreation?: Date;
  chantier?: Chantier;
  site?: Site;
  beneficiaire: Beneficiaire;
  referentCommercial?: Personne;
  personneAContacter?: Personne;
  proprietaire?: Personne;
  history?: DossierHistoryItem[];
  operations?: Operation[];
  attachments: Attachment[];
  personnePhysique?: boolean;
  streetViewUrl?: string | null;
  streetViewThumbnailUrl?: string | null;
  paymentTag?: DossierPaymentTag | null;
}

export interface Dossiers {
  records: DossierDetails[];
  totalSize: number;
  totalByStatus?: Record<DossierStatus, number>;
}

export interface DossierLead {
  typeOperation?: string;
  description?: string;
  beneficiaire: Beneficiaire;
  referentCommercial?: Personne;
  site: Site;
  batiment: Batiment;
  chantier: Chantier;
  optin?: boolean;
}

export interface CreateDossierLead {
  dossier: DossierLead;
  files: Base64File[];
}

export interface DepartmentCodeResponse {
  code: string;
}

export interface DossiersSearchParams
  extends PaginationParams, SortParams<DossierSortFields> {
  keyword?: string;
  referentsCommerciauxEmails?: string[];
  statuts?: string[];
  ficheDeLiaisonId?: string | null;
  companyId?: string;
  dateFinChantierMin?: string | null;
  dateFinChantierMax?: string | null;
  accountId?: string;
}
